/**
 * Cliente API de cierres anuales (Sprint 18 P16).
 *
 * Endpoints expuestos por el backend:
 * - GET /cierres-anuales/cuadre — cuadre anual de flujo de caja (2026-06).
 * - GET /cierres-anuales/{id} — detalle con saldos arrastrados.
 * - POST /cierres-anuales — ejecutar cierre (RBAC tesorero; usuario del token).
 * - PATCH /cierres-anuales/{id}/saldo-final-banco — Sprint 18 P16,
 *   tesorero registra/edita el saldo bancario al cierre.
 */
import { apiFetch } from './client';

export interface CierreAnualRead {
  id: string;
  anio: number;
  cerrado_at: string;
  cerrado_por_usuario_id: string;
  total_lotes_con_saldo: number;
  total_saldo_arrastrado: number;
  acta_url: string | null;
  /** Sprint 18 P16: CLP entero. Nullable si aún no se registró. */
  saldo_final_banco: number | null;
  creado_at: string;
  actualizado_at: string;
}

/**
 * Cuadre anual de flujo de caja (tesorería 2026-06). Derivado on-read:
 * por cada año cerrado, apertura (= saldo final del año anterior) +
 * recaudación − gastos − traspasos DAP = cierre calculado, contra el
 * saldo declarado del extracto. Montos CLP entero; `apertura`,
 * `cierre_calculado` y `diferencia` son null si falta la apertura o el
 * saldo declarado.
 */
export interface CuadreAnualItem {
  id: string;
  anio: number;
  cerrado_at: string;
  total_lotes_con_saldo: number;
  total_saldo_arrastrado: number;
  apertura: number | null;
  recaudacion: number;
  gastos: number;
  traspasos_dap: number;
  cierre_calculado: number | null;
  saldo_final_banco: number | null;
  diferencia: number | null;
}

export function getCuadreAnual(): Promise<CuadreAnualItem[]> {
  return apiFetch<CuadreAnualItem[]>('/cierres-anuales/cuadre');
}

/**
 * Ejecuta el cierre anual del año indicado (RBAC tesorero). El usuario
 * que cierra se deriva del token en el backend, no viaja en el body.
 * Acción irreversible: fija el saldo arrastrado y mueve el año contable
 * activo a `anio + 1`. Devuelve 409 si el año ya está cerrado.
 */
export function ejecutarCierreAnual(anio: number): Promise<CierreAnualRead> {
  return apiFetch<CierreAnualRead>('/cierres-anuales/', {
    method: 'POST',
    body: JSON.stringify({ anio }),
  });
}

export interface RegistrarSaldoFinalBancoInput {
  saldo_final_banco: number;
}

export function registrarSaldoFinalBanco(
  cierreId: string,
  data: RegistrarSaldoFinalBancoInput,
): Promise<CierreAnualRead> {
  return apiFetch<CierreAnualRead>(
    `/cierres-anuales/${cierreId}/saldo-final-banco`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    },
  );
}
